/** All admin permission values returned by the list API. */
export const ALL_USER_PERMISSIONS = [
  'assessment_manager',
  'bypass_max_days_for_edit_work',
  'can_bypass_w2_bonus_limit',
  'can_change_user_email',
  'can_create_w2_past_shift',
  'company_creator',
  'company_manager',
  'company_scope_manager',
  'company_switcher',
  'compliance_admin',
  'data_science_automation',
  'developer',
  'everify_agent',
  'finance_admin',
  'finance_specialist',
  'highnote_admin',
  'impersonate_user',
  'integration_manager',
  'marketplace_ops',
  'payment_admin',
  'permission_admin',
  'reset_i9',
  'supply_growth',
  'support_admin',
  'support_agent',
  'support_specialist',
  'user_chat',
  'user_files_viewer',
  'viewer',
] as const

export type UserPermission = (typeof ALL_USER_PERMISSIONS)[number]

/** Permissions required to actually use the `/user-permissions` UI */
export const UI_USER_PERMISSIONS = [
  'permission_admin',
  'support_admin',
  'support_specialist',
  'finance_admin',
  'company_scope_manager',
] as const satisfies readonly UserPermission[]

export type UIUserPermission = (typeof UI_USER_PERMISSIONS)[number]

/** Permissions that can be granted via POST /admin/permissions/grant/*. */
export const MANAGEABLE_PERMISSIONS = {
  permission_admin: [
    'assessment_manager',
    'bypass_max_days_for_edit_work',
    'can_bypass_w2_bonus_limit',
    'can_change_user_email',
    'can_create_w2_past_shift',
    'company_creator',
    'company_manager',
    'company_scope_manager',
    'company_switcher',
    'compliance_admin',
    'data_science_automation',
    'everify_agent',
    'finance_admin',
    'finance_specialist',
    'impersonate_user',
    'integration_manager',
    'marketplace_ops',
    'payment_admin',
    'reset_i9',
    'supply_growth',
    'support_admin',
    'support_agent',
    'support_specialist',
    'user_chat',
    'user_files_viewer',
    'viewer',
  ],
  support_admin: [
    'can_bypass_w2_bonus_limit',
    'can_change_user_email',
    'can_create_w2_past_shift',
    'support_agent',
    'support_specialist',
  ],
  support_specialist: ['support_agent'],
  finance_admin: ['finance_specialist'],
  company_scope_manager: ['company_creator', 'company_manager'],
} as const satisfies Record<UIUserPermission, readonly UserPermission[]>

export type UserPermissionOption = {
  value: UserPermission
  name: string
  description: string
}

export type UserPermissionMeta = {
  value: UserPermission
  name: string
  description: string
}

export const USER_PERMISSION_META: Record<UserPermission, UserPermissionMeta> =
  {
    assessment_manager: {
      value: 'assessment_manager',
      name: 'Assessment Manager',
      description:
        'Manage assessments associated with worker requirements (questionnaires, etc.).',
    },
    bypass_max_days_for_edit_work: {
      value: 'bypass_max_days_for_edit_work',
      name: 'Bypass Max Days For Edit Work',
      description: 'Edit work records beyond the normal max-days time window.',
    },
    can_bypass_w2_bonus_limit: {
      value: 'can_bypass_w2_bonus_limit',
      name: 'Bypass W2 Bonus Limit',
      description: 'Bypass W2 bonus payment limits.',
    },
    can_change_user_email: {
      value: 'can_change_user_email',
      name: 'Change User Email',
      description: 'Change a user email address.',
    },
    can_create_w2_past_shift: {
      value: 'can_create_w2_past_shift',
      name: 'Create W2 Past Shift',
      description: 'Create W2 shifts with start times in the past.',
    },
    company_creator: {
      value: 'company_creator',
      name: 'Company Creator',
      description:
        'Create new companies and company groups (intended for GTM, AMs, and AEs).',
    },
    company_manager: {
      value: 'company_manager',
      name: 'Company Manager',
      description:
        'Manage configuration for companies (e.g. Account Managers helping their clients).',
    },
    company_scope_manager: {
      value: 'company_scope_manager',
      name: 'Company Scope Manager',
      description: 'Grant and manage company scopes for all admin users.',
    },
    company_switcher: {
      value: 'company_switcher',
      name: 'Company Switcher',
      description: 'Switch between companies to navigate into business portal.',
    },
    compliance_admin: {
      value: 'compliance_admin',
      name: 'Compliance Admin',
      description: 'Manage compliance functions such as minimum wage.',
    },
    data_science_automation: {
      value: 'data_science_automation',
      name: 'Data Science Automation',
      description:
        'Intended for automation (bots) managed by Data Science team.',
    },
    developer: {
      value: 'developer',
      name: 'Developer',
      description: 'Intended for developer (engineer) testing.',
    },
    everify_agent: {
      value: 'everify_agent',
      name: 'E-Verify Agent',
      description:
        'Intended for admins that are responsible for E-Verify tasks and resolution.',
    },
    finance_admin: {
      value: 'finance_admin',
      name: 'Finance Admin',
      description: 'Grant and revoke finance specialist permissions.',
    },
    finance_specialist: {
      value: 'finance_specialist',
      name: 'Finance Specialist',
      description: 'Perform tasks for the general finance team (non admins).',
    },
    highnote_admin: {
      value: 'highnote_admin',
      name: 'Highnote Admin',
      description: 'Access Highnote card and banking admin capabilities.',
    },
    impersonate_user: {
      value: 'impersonate_user',
      name: 'Impersonate User',
      description:
        'Impersonate worker and client users to help debug app issues.',
    },
    integration_manager: {
      value: 'integration_manager',
      name: 'Integration Manager',
      description:
        'Manage (VMS) integrations such as Dayforce, VNDLY, Indeed Flex, etc.',
    },
    marketplace_ops: {
      value: 'marketplace_ops',
      name: 'Marketplace Operations',
      description: 'Members of the Marketplace Operations team.',
    },
    payment_admin: {
      value: 'payment_admin',
      name: 'Payment Admin',
      description: 'Manage user payments and payment-related admin tools.',
    },
    permission_admin: {
      value: 'permission_admin',
      name: 'Permission Admin',
      description:
        'Manage admin permission grants. Cannot be granted from this UI.',
    },
    reset_i9: {
      value: 'reset_i9',
      name: 'Reset I-9',
      description: "Ability to reset a worker's I-9 for W2 onboarding.",
    },
    supply_growth: {
      value: 'supply_growth',
      name: 'Supply Growth',
      description:
        'Manage referrals and other tools we use to increase our worker supply.',
    },
    support_admin: {
      value: 'support_admin',
      name: 'Support Admin',
      description: 'Access support admin capabilities.',
    },
    support_agent: {
      value: 'support_agent',
      name: 'Support Agent',
      description:
        'Access support agent capabilities (this is the broadest level of access).',
    },
    support_specialist: {
      value: 'support_specialist',
      name: 'Support Specialist',
      description:
        'Access support specialist capabilities (this is for BPO managers and HQ support).',
    },
    user_chat: {
      value: 'user_chat',
      name: 'User Chat',
      description:
        'Chat with workers and client users in the admin application.',
    },
    user_files_viewer: {
      value: 'user_files_viewer',
      name: 'User Files Viewer',
      description: 'List and view files stored for a given user.',
    },
    viewer: {
      value: 'viewer',
      name: '(Legacy) Viewer',
      description:
        'View information in admin app; does not grant ability to edit.',
    },
  }

export function getUserPermissionOptions(
  uiPermission: UIUserPermission
): UserPermissionOption[] {
  return MANAGEABLE_PERMISSIONS[uiPermission].map((permission) => ({
    value: permission,
    name: USER_PERMISSION_META[permission].name,
    description: USER_PERMISSION_META[permission].description,
  }))
}

export function getPermissionName(permission: UserPermission): string {
  return USER_PERMISSION_META[permission]?.name ?? permission
}

type hasPermissionFunc = (permission: UserPermission) => boolean

export function getUIPermission(
  hasPermission: hasPermissionFunc
): UIUserPermission | null {
  if (hasPermission('permission_admin')) {
    return 'permission_admin'
  }
  if (hasPermission('support_admin')) {
    return 'support_admin'
  }
  if (hasPermission('support_specialist')) {
    return 'support_specialist'
  }
  if (hasPermission('finance_admin')) {
    return 'finance_admin'
  }
  if (hasPermission('company_scope_manager')) {
    return 'company_scope_manager'
  }
  return null
}

/** Only permission_admin routes allow granting/revoking company scopes. */
export function canManageCompanyScopes(
  uiPermission: UIUserPermission | null
): boolean {
  return (
    uiPermission === 'permission_admin' ||
    uiPermission === 'company_scope_manager'
  )
}
